import type { BackendAdapter, BackendFilter, FilterValues } from "../../types";
import {
	pushBooleanFiltersIfSet,
	pushDateRangeFilters,
	pushInArrayOrExactFilterIfSet,
	pushNumberRangeFilters,
	pushPercentageRangeFilters,
} from "../../types";
import type { CountriesRow } from "./columns";

/**
 * Backend adapter for Countries tab.
 * Transforms filter values and sort config into the format expected by the API.
 */
export const countriesBackendAdapter: BackendAdapter<CountriesRow> = {
	/**
	 * Transform filter values into BackendFilter[] format.
	 * Only includes filters that have a value.
	 */
	buildFilters: (filterValues: FilterValues): BackendFilter[] => {
		const filters: BackendFilter[] = [];

		pushInArrayOrExactFilterIfSet(filters, "country", filterValues.country);

		// Exact match filters
		if (filterValues.continent) {
			filters.push({
				field: "continent",
				type: "exact",
				value: filterValues.continent,
			});
		}

		// Boolean filters
		pushBooleanFiltersIfSet(filters, filterValues, ["status"]);

		// Number range filters (integer counts, no conversion)
		pushNumberRangeFilters(filters, filterValues, [
			"totalPartnerClubs",
			"targetPartnerClubs",
			"numberOfScoutTeams",
			"numberOfDiscoverTeams",
			"ntPlayers",
			"totalGames",
			"gamesTagged",
			"viewsOfGames",
			"subscriptionsSold",
		]);

		// Percentage filters (0-100 input → 0.0-1.0 for API)
		pushPercentageRangeFilters(filters, filterValues, [
			"consistentRunningPlayerPercent",
			"variablePlayerQualityPercent",
			"percentOfGamesTagged",
			"proportionOfGameViews",
		]);

		// Decimal/Average filters (no conversion)
		pushNumberRangeFilters(filters, filterValues, [
			"avgGamePriority",
			"avgGameAnalysisProcessing",
		]);

		// Date range filters (datetime and date-only; backend uses datetimerange for both)
		pushDateRangeFilters(filters, filterValues, [
			"suggestedGamesUpdate",
			"playerToWatchUpdate",
			"seasonStart",
			"seasonEnd",
		]);

		return filters;
	},
};

